import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { FAQ_SECTIONS } from "@/data/faq";
import { FAQ_IMAGE } from "@/data/content";

// Render an answer string with support for:
//   \n\n  → paragraph break
//   • …   → bulleted list item
//   *…*   → italic accent line (single asterisks around a phrase)
function AnswerBody({ text }) {
  const paragraphs = text.split(/\n\n+/);
  return (
    <div className="space-y-4 text-bone/80 leading-relaxed text-[15px]">
      {paragraphs.map((block, i) => {
        const lines = block.split("\n");
        const bulletLines = lines.filter((l) => l.trim().startsWith("• "));
        if (bulletLines.length > 0 && bulletLines.length === lines.length) {
          return (
            <ul key={`p-${i}`} className="space-y-1.5 pl-1">
              {bulletLines.map((l, j) => (
                <li key={`b-${i}-${j}`} className="flex gap-3">
                  <span className="text-lavender mt-1.5">◆</span>
                  <span>{l.replace(/^•\s*/, "")}</span>
                </li>
              ))}
            </ul>
          );
        }
        const italicMatch = block.match(/^\*(.+)\*$/);
        if (italicMatch) {
          return (
            <p key={`p-${i}`} className="font-display italic text-lavender text-lg md:text-xl leading-snug">
              {italicMatch[1]}
            </p>
          );
        }
        return <p key={`p-${i}`}>{block}</p>;
      })}
    </div>
  );
}

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="relative">
      {/* HERO — full-bleed image with dark lavender overlay */}
      <section
        data-testid="faq-hero"
        className="relative min-h-[70svh] md:min-h-[80svh] flex items-end overflow-hidden"
      >
        <img
          src={FAQ_IMAGE}
          alt="Inside The Uncanny House"
          className="absolute inset-0 h-full w-full object-cover animate-slow-zoom"
        />
        {/* Layered overlays — obsidian at the bottom, lavender warmth on top */}
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/70 to-obsidian" />
        <div
          className="absolute inset-0 mix-blend-overlay opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 40%, rgba(183,166,200,0.35), transparent 60%)",
          }}
        />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pb-16 md:pb-24 pt-32 md:pt-40 w-full">
          <p className="label-eyebrow text-lavender animate-fade-up" style={{ animationDelay: "0.1s" }}>
            The House · Frequently Asked
          </p>
          <h1
            data-testid="faq-title"
            className="font-display text-bone text-5xl md:text-7xl lg:text-[92px] mt-6 leading-[0.95] max-w-4xl animate-fade-up"
            style={{ animationDelay: "0.25s" }}
          >
            Questions, <em className="not-italic text-lavender">quietly</em> answered.
          </h1>
          <p
            className="mt-6 font-display italic text-bone/85 text-xl md:text-2xl max-w-2xl animate-fade-up"
            style={{ animationDelay: "0.45s" }}
          >
            Everything you need to know before the first sip.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Sticky index (desktop) */}
        <aside className="hidden md:block md:col-span-3">
          <div className="sticky top-28">
            <p className="label-eyebrow text-lavender">Sections</p>
            <ul className="mt-6 space-y-3" data-testid="faq-index">
              {FAQ_SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`faq-index-${s.id}`}
                    className="block text-[12px] uppercase tracking-[0.28em] text-bone/60 hover:text-lavender transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-10 border-l-2 border-lavender/40 pl-5">
              <p className="font-display italic text-lavender text-lg leading-snug">
                The House is always listening.
              </p>
              <Link
                to="/chime-in"
                data-testid="faq-sidebar-chime-in"
                className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-bone hover:text-lavender transition-colors"
              >
                Chime In <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </aside>

        <div className="md:col-span-9 space-y-16 md:space-y-24">
          {FAQ_SECTIONS.map((section, idx) => (
            <section
              key={section.id}
              id={section.id}
              data-testid={`faq-section-${section.id}`}
              className="scroll-mt-28"
            >
              <div className="flex items-baseline gap-6">
                <span className="font-display italic text-lavender/60 text-2xl tabular-nums">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <h2 className="font-display text-bone text-3xl md:text-4xl leading-[1.1]">
                    {section.title}
                  </h2>
                  <div className="mt-4 h-px w-full bg-gradient-to-r from-lavender/60 via-lavender/20 to-transparent" />
                </div>
              </div>

              <Accordion type="multiple" className="mt-6" data-testid={`faq-accordion-${section.id}`}>
                {section.questions.map((qa, qi) => {
                  const value = `${section.id}-${qi}`;
                  return (
                    <AccordionItem
                      key={value}
                      value={value}
                      data-testid={`faq-item-${section.id}-${qi}`}
                      className="border-b border-bone/10 last:border-b-0"
                    >
                      <AccordionTrigger className="text-bone font-display text-lg md:text-xl leading-snug py-5 hover:no-underline hover:text-lavender data-[state=open]:text-lavender transition-colors">
                        {qa.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 pr-2">
                        <AnswerBody text={qa.a} />
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </section>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section
        data-testid="faq-closing"
        className="relative overflow-hidden border-t border-bone/10"
      >
        <img
          src={FAQ_IMAGE}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/90 via-obsidian/85 to-obsidian" />
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(183,166,200,0.25), transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 text-center">
          <p className="label-eyebrow text-lavender">Still Have a Question?</p>
          <h2 className="font-display text-bone text-4xl md:text-6xl mt-6 max-w-3xl mx-auto leading-[1.05]">
            Didn&apos;t find what you were looking for?
          </h2>
          <p className="mt-6 font-display italic text-lavender text-2xl md:text-3xl">
            Chime In.
          </p>
          <p className="mt-6 text-bone/75 max-w-xl mx-auto">
            Whether you have a question, an idea, or simply want to say hello, we&apos;d love to hear from you.
          </p>
          <p className="mt-3 text-bone/60 max-w-xl mx-auto italic font-display text-lg">
            The House is always listening.
          </p>
          <Link
            to="/chime-in"
            data-testid="faq-closing-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors"
          >
            Write to the House <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
